/**
 * Pusher / Laravel Echo plugin (Part G N5, client-only).
 *
 * Subscribes the authenticated user's Laravel notification channel
 * `private-App.Models.User.{id}`
 * and listens for Laravel's `BroadcastNotificationCreated` event. When a
 * notification arrives, the shared bell counter is refreshed and a browser
 * event is emitted so open notification lists can reload immediately.
 */

import {
  attachChannelErrorMonitoring,
  attachPusherErrorMonitoring,
  reportRealtimeError,
} from '~/utils/realtime-error-reporter';

export default defineNuxtPlugin(async (nuxtApp) => {
  const app = nuxtApp as unknown as ReturnType<typeof useNuxtApp>;
  const { data: authSession, status } = useAuth();
  const config = useRuntimeConfig();
  const pusherKey = (config.public as Record<string, unknown>).PUSHER_APP_KEY as string | undefined;
  const pusherCluster = (config.public as Record<string, unknown>).PUSHER_APP_CLUSTER as string | undefined;

  if (!pusherKey || !pusherCluster) {
    const message =
      '[notifications/pusher] PUSHER_APP_KEY or PUSHER_APP_CLUSTER not set - '
      + 'real-time notifications disabled. Bell falls back to fetchCount on page-load + tab-focus.';

    console.warn(message);
    return;
  }

  let PusherClient: any;
  let EchoClass: any;
  let subscribedUserId: number | string | null = null;

  try {
    PusherClient = (await import('pusher-js')).default;
    EchoClass = (await import('laravel-echo')).default;
  } catch (e) {
    const message =
      '[notifications/pusher] `pusher-js` and/or `laravel-echo` not installed - '
      + 'real-time notifications disabled. Run `npm i pusher-js laravel-echo` to enable.';

    console.warn(message);
    void reportRealtimeError(app, 'laravel_echo_initialization_failure', e, {
      extra: { message },
    });
    return;
  }

  const subscribe = (userId: number | string | undefined) => {
    if (!userId) return;
    if (String(subscribedUserId) === String(userId)) return;

    try {
      let pusherClient: any;

      try {
        pusherClient = new PusherClient(pusherKey, {
          cluster: pusherCluster,
          forceTLS: true,
          authEndpoint: '/api/broadcasting/auth',
          auth: {
            headers: {},
          },
        });
      } catch (e) {
        console.error('[notifications/pusher] failed to initialize Pusher client', e);
        void reportRealtimeError(app, 'pusher_initialization_failure', e);
        return;
      }

      attachPusherErrorMonitoring(app, pusherClient, { userId });

      let echo: any;

      try {
        echo = new EchoClass({
          broadcaster: 'pusher',
          client: pusherClient,
        });
      } catch (e) {
        console.error('[notifications/pusher] failed to initialize Laravel Echo', e);
        void reportRealtimeError(app, 'laravel_echo_initialization_failure', e);
        return;
      }

      const channelName = `private-App.Models.User.${userId}`;
      const channel = echo.private(`App.Models.User.${userId}`);
      attachChannelErrorMonitoring(app, channel, channelName);

      const { incrementCount, fetchCount } = useNotifications();

      channel.notification((notification: unknown) => {
        try {
          const sourceFeature =
            (notification as any)?.sourceFeature
            ?? (notification as any)?.source_feature
            ?? (notification as any)?.data?.sourceFeature
            ?? (notification as any)?.data?.source_feature
            ?? null;

          incrementCount(sourceFeature);
          void fetchCount();
          window.dispatchEvent(new CustomEvent('cleverdocs:notification-received', {
            detail: notification,
          }));
          console.debug('[notifications/pusher] received', notification);
        } catch (e) {
          console.error('[notifications/pusher] notification listener failed', e);
          void reportRealtimeError(app, 'event_listener_failure', e, {
            channelName,
            eventName: 'BroadcastNotificationCreated',
          });
        }
      });

      nuxtApp.provide('echo', echo);
      subscribedUserId = userId;
    } catch (e) {
      console.error('[notifications/pusher] failed to initialize Echo subscription', e);
      void reportRealtimeError(app, 'subscription_failure', e, {
        channelName: `private-App.Models.User.${userId}`,
      });
    }
  };

  const resolveUserId = (): number | string | undefined => {
    const sessionUser = (authSession.value as any)?.user;
    const authUser = (nuxtApp.$auth as any)?.user?.value ?? (nuxtApp.$auth as any)?.user;
    const user = sessionUser ?? authUser?.data ?? authUser;

    return user?.data?.id ?? user?.id ?? user?.user?.id;
  };

  const userId = resolveUserId();

  if (userId) {
    subscribe(userId);
  }

  watch(
    () => [status.value, resolveUserId()] as const,
    ([authStatus, nextUserId]) => {
      if (authStatus === 'authenticated' && nextUserId) {
        subscribe(nextUserId);
      }
    },
    { immediate: true },
  );
});
